// src/App.tsx - עם דף מחשבוני ייבוא אישי + מחשבון M1

import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

// Backoffice imports (unchanged)
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/shared/ProtectedRoute";
import LoginForm from "./components/backoffice/auth/LoginForm";
import Dashboard from "./components/backoffice/dashboard/Dashboard";
import UsersPage from "./components/backoffice/users/UsersPage";
import AdminsPage from "./components/backoffice/admins/AdminsPage";
import CalculationParametersPage from "./components/backoffice/calculation-parameters/CalculationParametersPage";
import ExplanationsPage from "./components/backoffice/explanations/ExplanationsPage";
import Layout from "./components/backoffice/layout/Layout";
import EPADataPage from "./components/backoffice/epa-data/EPADataPage";
import CouponsPage from "./components/backoffice/coupons/CouponsPage";
import LandingPage from "./components/LandingPage";
import DevLandingPage from "./components/dev-landing/DevLandingPage";

// Enduser imports
import { enduserAuthService } from "./services/enduser/enduserAuthService";
import { EnduserAuthProvider } from "./contexts/EnduserAuthContext";
import AuthPage from "./components/enduser/auth/AuthPage";
import ImportSelectionPage from "./components/enduser/dashboard/ImportSelectionPage";
import EnduserLayout from "./components/enduser/Layout/EnduserLayout";
import EnduserProtectedRoute from "./components/shared/EnduserProtectedRoute";
import AccessibilityWidget from "./components/shared/AccessibilityWidget";
import TermsAndPrivacyPage from "./components/shared/TermsAndPrivacyPage";
import AccessibilityStatementPage from "./components/shared/AccessibilityStatementPage";
import CalculationExplanationPage from "./components/shared/CalculationExplanationPage";
import AboutPage from "./components/shared/AboutPage";
import SubscriptionPage from "./components/enduser/subscription/SubscriptionPage";
import FreeTrialFlow from "./components/enduser/subscription/FreeTrialFlow";
import PaymentSuccessPage from "./components/enduser/subscription/PaymentSuccessPage";
import PaymentCancelPage from "./components/enduser/subscription/PaymentCancelPage";
import PersonalImportCalculatorsPage from "./components/enduser/calculators/PersonalImportCalculatorsPage";
import CommercialImportCalculatorsPage from "./components/enduser/calculators/CommercialImportCalculatorsPage";
import M1VehicleCalculatorPage from "./components/enduser/calculators/M1VehicleCalculatorPage";
import CollectorVehicleCalculatorPage from "./components/enduser/calculators/CollectorVehicleCalculatorPage";
import TrailerCalculatorPage from "./components/enduser/calculators/TrailerCalculatorPage";
import TaxiCalculatorPage from "./components/enduser/calculators/TaxiCalculatorPage";
import JetSkiCalculatorPage from "./components/enduser/calculators/JetSkiCalculatorPage";
import MotorcycleCalculatorPage from "./components/enduser/calculators/MotorcycleCalculatorPage";
import N1TenderCalculatorPage from "./components/enduser/calculators/N1TenderCalculatorPage";
import M1PrivateElectricCalculatorPage from "./components/enduser/calculators/M1PrivateElectricCalculatorPage";
import N1PrivateElectricCalculatorPage from "./components/enduser/calculators/N1PrivateElectricCalculatorPage";
import MotorcycleCommercialCalculatorPage from "./components/enduser/calculators/MotorcycleCommercialCalculatorPage";
import TaxiCommercialCalculatorPage from "./components/enduser/calculators/TaxiCommercialCalculatorPage";
import JetSkiCommercialCalculatorPage from "./components/enduser/calculators/JetSkiCommercialCalculatorPage";
import M1CommercialCalculatorPage from "./components/enduser/calculators/M1CommercialCalculatorPage";
import N1N2CommercialCalculatorPage from "./components/enduser/calculators/N1N2CommercialCalculatorPage";
import M1ElectricCommercialCalculatorPage from "./components/enduser/calculators/M1ElectricCommercialCalculatorPage";
import N1N2ElectricCommercialCalculatorPage from "./components/enduser/calculators/N1N2ElectricCommercialCalculatorPage";
import ProfilePage from "./components/enduser/profile/ProfilePage";
import CalculationHistoryPage from "./components/enduser/history/CalculationHistoryPage";
import CalculationResultViewPage from "./components/enduser/history/CalculationResultViewPage";
import RecommendedContactsPage from "./components/shared/RecommendedContactsPage";

import ArticlesPage from "./components/articles/ArticlesPage";
import ArticlePage from "./components/articles/ArticlePage";

import ArticlesListPage from "./components/backoffice/articles/ArticlesListPage";
import ArticleEditorPage from "./components/backoffice/articles/ArticleEditorPage";
import CategoriesPage from "./components/backoffice/articles/CategoriesPage";
import ArticlePreviewPage from "./components/backoffice/articles/ArticlePreviewPage";
// Redirect to dashboard if already logged in
const AuthPageRedirect: React.FC = () => {
  const [shouldRedirect, setShouldRedirect] = React.useState(false);
  const [isChecking, setIsChecking] = React.useState(true);

  React.useEffect(() => {
    const checkAuth = async () => {
      const token = enduserAuthService.getSessionToken();
      if (token) {
        const result = await enduserAuthService.verifySession();
        if (result.res === "OK") {
          setShouldRedirect(true);
        }
      }
      setIsChecking(false);
    };
    checkAuth();
  }, []);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (shouldRedirect) {
    return <Navigate to="/enduser/dashboard" replace />;
  }

  return <AuthPage />;
};
function App() {
  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        {/* Accessibility Widget - Available on all pages */}
        <AccessibilityWidget />

        <Routes>
          {/* דף נחיתה ראשי */}
          <Route path="/" element={<LandingPage />} />
          {/* דף נחיתה חדש בפיתוח (WIP) — לא מאונדקס ב-Google */}
          <Route path="/dev_lp" element={<DevLandingPage />} />
          {/* תנאי שימוש ומדיניות פרטיות - נגיש לכולם */}
          <Route path="/terms-and-privacy" element={<TermsAndPrivacyPage />} />
          {/* הצהרת נגישות - נגישה לכולם */}
          <Route
            path="/accessibility-statement"
            element={<AccessibilityStatementPage />}
          />
          {/* דף אודות */}
          <Route path="/about" element={<AboutPage />} />
          {/* אנשי קשר מומלצים - נגיש לכולם */}
          <Route
            path="/recommended-contacts"
            element={<RecommendedContactsPage />}
          />
          <Route path="/articles" element={<ArticlesPage />} />
          <Route path="/articles/:slug" element={<ArticlePage />} />
          {/* Payment result pages - outside EnduserLayout (user comes from external Grow page) */}
          <Route
            path="/payment-success"
            element={
              <EnduserAuthProvider>
                <PaymentSuccessPage />
              </EnduserAuthProvider>
            }
          />
          <Route
            path="/payment-cancel"
            element={
              <EnduserAuthProvider>
                <PaymentCancelPage />
              </EnduserAuthProvider>
            }
          />
          {/* מערכת הניהול - Backoffice (unchanged) */}
          <Route
            path="/backoffice/*"
            element={
              <AuthProvider>
                <Routes>
                  <Route path="login" element={<LoginForm />} />
                  {/* תצוגה מקדימה — מוגנת בסשן, אך ללא תפריט הבקאופיס,
                      כדי שהמאמר ייראה כמו שהקורא רואה אותו */}
                  <Route
                    path="articles/preview/:id"
                    element={
                      <ProtectedRoute>
                        <ArticlePreviewPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="*"
                    element={
                      <ProtectedRoute>
                        <Layout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Dashboard />} />
                    <Route path="users" element={<UsersPage />} />
                    <Route path="admins" element={<AdminsPage />} />
                    <Route
                      path="calculation-parameters"
                      element={<CalculationParametersPage />}
                    />
                    <Route path="explanations" element={<ExplanationsPage />} />
                    <Route path="epa-data" element={<EPADataPage />} />
                    <Route path="articles" element={<ArticlesListPage />} />
                    <Route
                      path="articles/new"
                      element={<ArticleEditorPage />}
                    />
                    <Route
                      path="articles/edit/:id"
                      element={<ArticleEditorPage />}
                    />
                    <Route
                      path="articles/categories"
                      element={<CategoriesPage />}
                    />
                    <Route path="coupons" element={<CouponsPage />} />
                  </Route>
                </Routes>
              </AuthProvider>
            }
          />

          {/* מערכת משתמשי הקצה - Real Auth Integration */}
          <Route
            path="/enduser/*"
            element={
              <EnduserAuthProvider>
                <Routes>
                  {/* דף התחברות/הרשמה - ללא הגנה */}
                  <Route path="login" element={<AuthPageRedirect />} />
                  <Route path="auth" element={<AuthPageRedirect />} />
                  {/* Protected routes with layout */}
                  <Route
                    path="*"
                    element={
                      <EnduserProtectedRoute>
                        <EnduserLayout />
                      </EnduserProtectedRoute>
                    }
                  >
                    <Route path="dashboard" element={<ImportSelectionPage />} />

                    {/* Personal Area */}
                    <Route path="profile" element={<ProfilePage />} />

                    {/* Calculators pages with requireCalculatorAccess */}
                    <Route
                      path="calculators/personal-import"
                      element={
                        <EnduserProtectedRoute requireCalculatorAccess={true}>
                          <PersonalImportCalculatorsPage />
                        </EnduserProtectedRoute>
                      }
                    />

                    {/* Commercial Import Calculators - placeholder for future */}
                    {/* Commercial Import Calculators */}
                    <Route
                      path="calculators/commercial-import"
                      element={
                        <EnduserProtectedRoute requireCalculatorAccess={true}>
                          <CommercialImportCalculatorsPage />
                        </EnduserProtectedRoute>
                      }
                    />

                    {/* === SPECIFIC CALCULATOR ROUTES === */}
                    {/* M1 Vehicle Calculator - רכב בנזין/דיזל */}
                    <Route
                      path="calculator/m1-petrol-diesel"
                      element={
                        <EnduserProtectedRoute requireCalculatorAccess={true}>
                          <M1VehicleCalculatorPage />
                        </EnduserProtectedRoute>
                      }
                    />
                    {/* M1 Commercial Calculator - רכב בנזין/דיזל/היברידי ייבוא מסחרי */}
                    <Route
                      path="calculator/m1-commercial"
                      element={
                        <EnduserProtectedRoute requireCalculatorAccess={true}>
                          <M1CommercialCalculatorPage />
                        </EnduserProtectedRoute>
                      }
                    />
                    {/* M1 Electric Commercial Calculator - רכב חשמלי ייבוא מסחרי */}
                    <Route
                      path="calculator/m1-electric-commercial"
                      element={
                        <EnduserProtectedRoute requireCalculatorAccess={true}>
                          <M1ElectricCommercialCalculatorPage />
                        </EnduserProtectedRoute>
                      }
                    />
                    {/* N1 & N2 Electric Commercial Calculator - רכב משא חשמלי N1 & N2 ייבוא מסחרי */}
                    <Route
                      path="calculator/n1-n2-electric-commercial"
                      element={
                        <EnduserProtectedRoute requireCalculatorAccess={true}>
                          <N1N2ElectricCommercialCalculatorPage />
                        </EnduserProtectedRoute>
                      }
                    />
                    {/* N1N2 Commercial Calculator - רכב משא N1 & N2 ייבוא מסחרי */}
                    <Route
                      path="calculators/n1n2-commercial"
                      element={
                        <EnduserProtectedRoute requireCalculatorAccess={true}>
                          <N1N2CommercialCalculatorPage />
                        </EnduserProtectedRoute>
                      }
                    />
                    {/* N1 Tender Calculator - טנדר/רכב משא קל */}
                    <Route
                      path="calculator/n1-tender"
                      element={
                        <EnduserProtectedRoute requireCalculatorAccess={true}>
                          <N1TenderCalculatorPage />
                        </EnduserProtectedRoute>
                      }
                    />
                    {/* מחשבון רכבי אספנות */}
                    <Route
                      path="calculator/collector-vehicle"
                      element={
                        <EnduserProtectedRoute requireCalculatorAccess={true}>
                          <CollectorVehicleCalculatorPage />
                        </EnduserProtectedRoute>
                      }
                    />
                    {/* מחשבון נגררים */}
                    <Route
                      path="calculator/trailer"
                      element={
                        <EnduserProtectedRoute requireCalculatorAccess={true}>
                          <TrailerCalculatorPage />
                        </EnduserProtectedRoute>
                      }
                    />
                    {/* Placeholder routes for other calculators */}
                    {/* <Route
                      path="calculator/electric-truck"
                      element={
                        <EnduserProtectedRoute requireCalculatorAccess={true}>
                          <div className="p-8 text-center" dir="rtl">
                            <h2 className="text-2xl font-bold mb-4">
                              🚛⚡ רכב משא חשמלי
                            </h2>
                            <p className="text-gray-600">המחשבון בפיתוח...</p>
                            <div className="mt-4">
                              <button
                                onClick={() => window.history.back()}
                                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                              >
                                חזור
                              </button>
                            </div>
                          </div>
                        </EnduserProtectedRoute>
                      }
                    /> */}

                    <Route
                      path="calculator/regular-truck"
                      element={
                        <EnduserProtectedRoute requireCalculatorAccess={true}>
                          <div className="p-8 text-center" dir="rtl">
                            <h2 className="text-2xl font-bold mb-4">
                              🚛 רכב משא רגיל
                            </h2>
                            <p className="text-gray-600">המחשבון בפיתוח...</p>
                            <div className="mt-4">
                              <button
                                onClick={() => window.history.back()}
                                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                              >
                                חזור
                              </button>
                            </div>
                          </div>
                        </EnduserProtectedRoute>
                      }
                    />

                    <Route
                      path="calculator/m1-electric"
                      element={
                        <EnduserProtectedRoute requireCalculatorAccess={true}>
                          <M1PrivateElectricCalculatorPage />
                        </EnduserProtectedRoute>
                      }
                    />
                    <Route
                      path="calculator/n1-electric"
                      element={
                        <EnduserProtectedRoute requireCalculatorAccess={true}>
                          <N1PrivateElectricCalculatorPage />
                        </EnduserProtectedRoute>
                      }
                    />
                    <Route
                      path="calculator/taxi"
                      element={
                        <EnduserProtectedRoute requireCalculatorAccess={true}>
                          <TaxiCalculatorPage />
                        </EnduserProtectedRoute>
                      }
                    />
                    <Route
                      path="calculator/taxi-commercial"
                      element={
                        <EnduserProtectedRoute requireCalculatorAccess={true}>
                          <TaxiCommercialCalculatorPage />
                        </EnduserProtectedRoute>
                      }
                    />
                    <Route
                      path="calculator/jet-ski"
                      element={
                        <EnduserProtectedRoute requireCalculatorAccess={true}>
                          <JetSkiCalculatorPage />
                        </EnduserProtectedRoute>
                      }
                    />

                    {/* <Route 
                    path="/enduser/calculator/collector-vehicle" 
                    element={<CollectorVehicleCalculatorPage />} 
                  /> */}

                    <Route
                      path="calculator/motorcycle"
                      element={
                        <EnduserProtectedRoute requireCalculatorAccess={true}>
                          <MotorcycleCalculatorPage />
                        </EnduserProtectedRoute>
                      }
                    />
                    <Route
                      path="calculator/motorcycle-commercial"
                      element={
                        <EnduserProtectedRoute requireCalculatorAccess={true}>
                          <MotorcycleCommercialCalculatorPage />
                        </EnduserProtectedRoute>
                      }
                    />
                    <Route
                      path="calculator/jet-ski-commercial"
                      element={
                        <EnduserProtectedRoute requireCalculatorAccess={true}>
                          <JetSkiCommercialCalculatorPage />
                        </EnduserProtectedRoute>
                      }
                    />
                    {/* Generic fallback for undefined calculator types */}
                    <Route
                      path="calculator/:calculatorType"
                      element={
                        <EnduserProtectedRoute requireCalculatorAccess={true}>
                          <div className="p-8 text-center" dir="rtl">
                            <h2 className="text-2xl font-bold mb-4">
                              🧮 מחשבון
                            </h2>
                            <p className="text-gray-600">
                              המחשבון שביקשת לא קיים או עדיין בפיתוח
                            </p>
                            <div className="mt-4">
                              <button
                                onClick={() => window.history.back()}
                                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                              >
                                חזור
                              </button>
                            </div>
                          </div>
                        </EnduserProtectedRoute>
                      }
                    />

                    {/* History page */}
                    {/* <Route
                      path="history"
                      element={
                        <div className="p-8 text-center" dir="rtl">
                          <h2 className="text-2xl font-bold mb-4">
                            📊 היסטוריית חישובים
                          </h2>
                          <p className="text-gray-600">בפיתוח...</p>
                        </div>
                      }
                    /> */}
                    <Route
                      path="history"
                      element={<CalculationHistoryPage />}
                    />
                    <Route
                      path="calculation-result/:id"
                      element={<CalculationResultViewPage />}
                    />
                    {/* Explanations page */}
                    <Route
                      path="explanations"
                      element={<CalculationExplanationPage />}
                    />

                    {/* Subscription page */}
                    <Route path="subscription" element={<SubscriptionPage />} />

                    {/* Free trial flow - מסך מקדים + הזנת קוד (ללא requireCalculatorAccess) */}
                    <Route path="free-trial" element={<FreeTrialFlow />} />

                    {/* Default enduser route */}
                    <Route
                      index
                      element={<Navigate to="dashboard" replace />}
                    />
                  </Route>
                </Routes>
              </EnduserAuthProvider>
            }
          />

          {/* דף  */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
